/**
 * Lays out one stacked bar's segments in pixels so that every non-zero segment is at least
 * `minPx` tall (and therefore hoverable), while the whole bar still fills exactly `pixelHeight`
 * (so the bar top still lands on the true total and matches the axis).
 *
 * Tiny tenants in a stacked storage/usage bar otherwise render sub-pixel and cannot be hovered or
 * clicked. Here any segment whose proportional height would fall below the minimum is pinned to the
 * minimum and the remaining height is shared out among the rest in proportion to their values. If
 * the bar is too short to give every non-zero segment the minimum, the minimum is reduced to an
 * equal share so the segments stay proportional-ish and still sum to `pixelHeight`.
 *
 * Returns one height per input value, aligned to `values`; zero (or negative) values get height 0.
 */
export function stackLayout(values: number[], pixelHeight: number, minPx: number): number[] {
  const result = new Array(values.length).fill(0);
  if (pixelHeight <= 0) {
    return result;
  }

  const nonZero = values
    .map((value, index) => ({ index, value }))
    .filter((v) => v.value > 0);

  if (!nonZero.length) {
    return result;
  }

  // The minimum can never exceed an equal share, or the pinned minimums would overflow the bar.
  const effectiveMin = Math.min(minPx, pixelHeight / nonZero.length);

  // Water-fill: repeatedly pin any segment whose proportional share of the still-free height falls
  // below the minimum, then re-share the rest, until no more need pinning. Bounded by the segment
  // count (each pass pins at least one), so it terminates quickly for the handful of series here.
  const pinned = new Set<number>();
  for (;;) {
    const freeHeight = pixelHeight - pinned.size * effectiveMin;
    const freeValueSum = nonZero
      .filter((v) => !pinned.has(v.index))
      .reduce((sum, v) => sum + v.value, 0);

    let pinnedThisPass = false;
    for (const v of nonZero) {
      if (pinned.has(v.index)) {
        continue;
      }
      const share = freeValueSum > 0 ? (v.value / freeValueSum) * freeHeight : 0;
      if (share < effectiveMin) {
        pinned.add(v.index);
        pinnedThisPass = true;
      }
    }

    if (!pinnedThisPass) {
      for (const v of nonZero) {
        result[v.index] = pinned.has(v.index)
          ? effectiveMin
          : freeValueSum > 0
            ? (v.value / freeValueSum) * freeHeight
            : 0;
      }
      return result;
    }
  }
}
